import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Chip,
  Button,
  Grid,
  Alert
} from '@mui/material';
import {
  Sync,
  CheckCircle,
  Error as ErrorIcon,
  Schedule,
  Refresh
} from '@mui/icons-material';
import { format } from 'date-fns';
import api from '../../../../../utils/api';

const SyncStatus = ({ connection }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (connection) {
      loadStatus();
      const interval = setInterval(loadStatus, 5000); // Poll every 5 seconds
      return () => clearInterval(interval);
    }
  }, [connection]);

  const loadStatus = async () => {
    try {
      const response = await api.get(`/shopify/sync/status/${connection.id}`);
      if (response.data.success) {
        setStatus(response.data.data);
      }
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load sync status');
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setLoading(true);
    loadStatus();
  };

  const getStatusIcon = () => {
    if (!status) return <Schedule />;
    
    switch (status.syncStatus) {
      case 'syncing':
        return <Sync className="spin" />;
      case 'error':
        return <ErrorIcon color="error" />;
      case 'idle':
        return <CheckCircle color="success" />;
      default:
        return <Schedule />;
    }
  };

  const getStatusColor = () => {
    if (!status) return 'default';
    
    switch (status.syncStatus) {
      case 'syncing':
        return 'primary';
      case 'error':
        return 'error';
      case 'idle':
        return 'success';
      default:
        return 'default';
    }
  };

  if (loading && !status) {
    return (
      <Card>
        <CardContent>
          <LinearProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Loading sync status...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">
              Sync Status
            </Typography>
            <Button
              size="small"
              startIcon={<Refresh />}
              onClick={handleRefresh}
              disabled={loading}
            >
              Refresh
            </Button>
          </Box>

          <Grid container spacing={3}>
            {/* Current Status */}
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {getStatusIcon()}
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Current Status
                  </Typography>
                  <Chip
                    label={status?.syncStatus || 'Unknown'}
                    color={getStatusColor()}
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                </Box>
              </Box>
            </Grid>

            {/* Last Sync */}
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                Last Sync
              </Typography>
              <Typography variant="body1">
                {status?.lastSyncAt
                  ? format(new Date(status.lastSyncAt), 'MMM d, yyyy HH:mm:ss')
                  : 'Never'}
              </Typography>
            </Grid>

            {/* Error Message */}
            {status?.lastError && (
              <Grid item xs={12}>
                <Alert severity="error">
                  <Typography variant="body2">
                    <strong>Last Error:</strong> {status.lastError}
                  </Typography>
                </Alert>
              </Grid>
            )}

            {/* Sync Progress */}
            {status?.syncStatus === 'syncing' && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Sync in Progress
                </Typography>
                <LinearProgress />
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      <style jsx>{`
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        .spin {
          animation: spin 2s linear infinite;
        }
      `}</style>
    </Box>
  );
};

export default SyncStatus;
